/**
 * Pure slot operations of the editor draft (the applyGatewayOp pattern) —
 * Phase 21: one list of drops, each naming its groups. Add / fill continue
 * the pattern of the list (the next group in turn, or every group when the
 * drops send to every group); quick fills write the two usual patterns;
 * unlinking a group takes it off every drop, its own amounts too.
 * For Media Devoted | Created By Ciro Autiero
 */
import { arrayMove } from '@dnd-kit/sortable';
import type { DropGenSlotRow } from '@trafficpulse/shared';

import type { ContainerDraft, DraftSlot } from '../../../../drop-generator.types';
import { FILL_SLOT_CAP } from '../../../../utils/drops/fill-count';
import { dropKey, onlyKey } from './group-keyed';

export interface SlotPatch {
  routeId?: number | null;
  gatewayRowId?: string | null;
  listsByGroup?: Record<string, number> | null;
  amount?: number | null;
  amountsByGroup?: Record<string, number> | null;
  enabled?: boolean;
  listGroupIds?: string[];
  pinnedAtMin?: number | null;
  sendOutsideWindow?: boolean;
  listsRankByGroup?: Record<string, number> | null;
}

export type SlotAction =
  | { type: 'slotAdd' }
  | { type: 'slotAddMany'; count: number }
  | { type: 'slotRemove'; index: number }
  | { type: 'slotPatch'; index: number; patch: SlotPatch }
  | { type: 'slotMove'; from: number; to: number }
  /** `order` = the drops' positions in the order the list reads (time order). */
  | { type: 'slotsAlternate'; order?: number[] }
  | { type: 'slotsAllGroups' };

export const newSlot = (listGroupIds: string[] = []): DraftSlot => ({
  id: null,
  amount: null,
  listsByGroup: null,
  amountsByGroup: null,
  enabled: true,
  routeId: null,
  gatewayRowId: null,
  optionsOverrideJson: null,
  listGroupIds,
  pinnedAtMin: null,
  sendOutsideWindow: false,
  listsRankByGroup: null,
});

/** A saved slot as the editor's draft holds it (a stable dirty baseline). */
export const slotToDraft = (s: DropGenSlotRow): DraftSlot => ({
  id: s.id,
  listsByGroup: s.listsByGroupJson ?? null,
  amount: s.amount,
  amountsByGroup: s.amountsByGroup ?? null,
  enabled: s.enabled,
  routeId: s.routeId,
  gatewayRowId: s.gatewayRowId ?? null,
  optionsOverrideJson: s.optionsOverrideJson ?? null,
  listGroupIds: s.listGroupIds ?? [],
  pinnedAtMin: s.pinnedAtMin ?? null,
  sendOutsideWindow: s.sendOutsideWindow ?? false,
  listsRankByGroup: s.listsRankByGroupJson ?? null,
});

export function applySlotOp(draft: ContainerDraft, action: SlotAction): ContainerDraft {
  const slots = draft.slots;
  const links = draft.groups.map((g) => g.listGroupId);
  switch (action.type) {
    case 'slotAdd':
      return withSlots(draft, [...slots, newSlot(nextGroups(links, slots))]);
    case 'slotAddMany':
      return fillCount(action.count) === 0 ? draft : withSlots(draft, addMany(links, slots, action.count));
    case 'slotRemove':
      return withSlots(
        draft,
        slots.filter((_, i) => i !== action.index),
      );
    case 'slotPatch':
      return withSlots(
        draft,
        slots.map((s, i) => (i === action.index ? { ...s, ...action.patch } : s)),
      );
    case 'slotMove':
      return withSlots(draft, arrayMove(slots, action.from, action.to));
    case 'slotsAlternate':
      return withSlots(draft, alternate(links, slots, action.order ?? slots.map((_, i) => i)));
    case 'slotsAllGroups':
      return withSlots(
        draft,
        slots.map((s) => ({ ...s, listGroupIds: [...links] })),
      );
    default:
      return draft;
  }
}

/** What a new drop sends to: every group when the last drop on the clock
 *  did, else the next one in turn (a pinned drop keeps its own time, so it
 *  is not "the drop before" a new one). */
export function nextGroups(links: string[], slots: DraftSlot[]): string[] {
  if (links.length === 0) return [];
  const last = [...slots].reverse().find((s) => s.enabled && s.pinnedAtMin === null && s.listGroupIds.length > 0);
  if (!last) return [links[0]];
  if (links.every((g) => last.listGroupIds.includes(g))) return [...links];
  const i = links.indexOf(last.listGroupIds[last.listGroupIds.length - 1]);
  return [links[(i + 1) % links.length]];
}

const fillCount = (count: number): number => Math.min(Math.max(0, Math.floor(count)), FILL_SLOT_CAP);

function addMany(links: string[], slots: DraftSlot[], count: number): DraftSlot[] {
  const n = fillCount(count);
  const out = [...slots];
  for (let i = 0; i < n; i += 1) out.push(newSlot(nextGroups(links, out)));
  return out;
}

/** "Alternate groups": one group per drop, in turn down the list as it reads
 *  (pinned drops in their time place), starting from the group the first
 *  drop already has (user, 11/10/2026) — else the first linked group; a
 *  switched-off drop takes no turn. */
function alternate(links: string[], slots: DraftSlot[], order: number[]): DraftSlot[] {
  if (links.length === 0) return slots;
  const out = [...slots];
  let turn = firstTurn(links, order.length > 0 ? slots[order[0]] : undefined);
  for (const i of order) {
    const group = links[turn % links.length];
    const own = slots[i].amountsByGroup?.[group];
    out[i] = {
      ...slots[i],
      listGroupIds: [group],
      listsByGroup: onlyKey(slots[i].listsByGroup, group),
      listsRankByGroup: onlyKey(slots[i].listsRankByGroup, group),
      amountsByGroup: own === undefined ? null : { [group]: own },
    };
    if (slots[i].enabled) turn += 1;
  }
  return out;
}

/** Where the alternation starts: the first drop's group (the first of its groups in link order), else 0. */
function firstTurn(links: string[], first: DraftSlot | undefined): number {
  const i = links.findIndex((g) => first?.listGroupIds.includes(g));
  return Math.max(0, i);
}

/** Unlinking a group: it leaves every drop, with its own amounts. */
export function withoutGroup(slots: DraftSlot[], groupId: string): DraftSlot[] {
  return slots.map((s) => {
    const own = s.amountsByGroup ? { ...s.amountsByGroup } : null;
    if (own) delete own[groupId];
    return {
      ...s,
      listsByGroup: dropKey(s.listsByGroup, groupId),
      listsRankByGroup: dropKey(s.listsRankByGroup, groupId),
      listGroupIds: s.listGroupIds.filter((g) => g !== groupId),
      amountsByGroup: own && Object.keys(own).length > 0 ? own : null,
    };
  });
}

const withSlots = (draft: ContainerDraft, slots: DraftSlot[]): ContainerDraft => ({ ...draft, slots });
